/**
 * Debounced URL metadata fetch and one-time title prefill when reference URL changes.
 */
import { useEffect, useRef } from "react";
import { useUrlMetadata } from "hooks/useUrlMetadata";

export interface UseUrlTitlePrefillParams {
  /** When true, metadata is fetched for `referenceLink` */
  linkEnabled: boolean;
  referenceLink: string;
  /**
   * When true (impact-style), only prefill title when `titleEnabled` is also true.
   * When false (solution-style), prefill whenever the link field is enabled.
   */
  titleToggleRequiredForPrefill: boolean;
  /** When `titleToggleRequiredForPrefill` is true, must also be true to apply meta title */
  titleEnabled: boolean;
  setTitle: (value: string) => void;
}

/**
 * Wires `useUrlMetadata` debounced fetch and applies `meta.title` to title state
 * when the reference URL changes (guarded by ref so we do not overwrite user edits on re-renders).
 */
export function useUrlTitlePrefill(params: UseUrlTitlePrefillParams): ReturnType<typeof useUrlMetadata> {
  const { linkEnabled, referenceLink, titleToggleRequiredForPrefill, titleEnabled, setTitle } = params;
  const { meta, setMeta, loading, fetchNow, fetchDebounced } = useUrlMetadata();
  const lastAppliedTitleUrlRef = useRef<string | null>(null);

  useEffect(() => {
    if (linkEnabled && referenceLink) {
      fetchDebounced(referenceLink);
    } else {
      fetchDebounced.cancel();
    }
    return () => fetchDebounced.cancel();
  }, [referenceLink, linkEnabled, fetchDebounced]);

  useEffect(() => {
    if (!linkEnabled) return;
    if (titleToggleRequiredForPrefill && !titleEnabled) return;
    if (!referenceLink || !meta?.title) return;
    if (lastAppliedTitleUrlRef.current === referenceLink) return;
    setTitle(meta.title);
    lastAppliedTitleUrlRef.current = referenceLink;
  }, [meta?.title, referenceLink, linkEnabled, titleEnabled, titleToggleRequiredForPrefill, setTitle]);

  return { meta, setMeta, loading, fetchNow, fetchDebounced };
}
